/**
 * GIT STORE
 *
 * Manages GitHub integration state for Factory projects.
 * Handles installations, repos, branches, commits, and PRs.
 * Supports both GitHub App (installation) and PAT authentication.
 */

import { create } from "zustand";
import { subscribeWithSelector, persist } from "zustand/middleware";
import {
  githubApi,
  GitHubInstallation,
  GitHubRepo,
  GitHubBranch,
  GitHubCommit,
  GitHubPullRequest,
  GitHubPAT,
  GitHubPATValidation,
  AuthType,
} from "@/lib/github-api";
import { isTauri } from "@/hooks/usePlatform";

// ============================================================================
// Types
// ============================================================================

export type GitTabState =
  | "NO_CONNECTION"
  | "NO_REPO"
  | "LOADING"
  | "READY"
  | "ERROR";

// Specific error codes from backend
export type GitErrorCode =
  | "RATE_LIMITED"
  | "TOKEN_EXPIRED"
  | "APP_NOT_INSTALLED"
  | "INSTALLATION_REMOVED"
  | "ACCESS_DENIED"
  | "NOT_FOUND"
  | "UNKNOWN";

interface CacheEntry<T> {
  data: T;
  fetchedAt: number;
  isStale: boolean;
}

interface GitError {
  message: string;
  code: GitErrorCode;
  retryAfter?: number;
}

export type { AuthType };

function parseError(error: unknown): GitError {
  const message = (error as Error).message || "Unknown error";

  // Parse rate limit from message
  if (message.includes("Rate limit") || message.includes("429")) {
    const match = message.match(/(\d+)s/);
    return {
      message,
      code: "RATE_LIMITED",
      retryAfter: match ? parseInt(match[1], 10) : 60,
    };
  }

  // Parse specific error codes
  if (message.includes("authentication expired") || message.includes("401")) {
    return { message, code: "TOKEN_EXPIRED" };
  }
  if (message.includes("not installed") || message.includes("APP_NOT_INSTALLED")) {
    return { message, code: "APP_NOT_INSTALLED" };
  }
  if (message.includes("installation") && message.includes("removed")) {
    return { message, code: "INSTALLATION_REMOVED" };
  }
  if (message.includes("Access denied") || message.includes("403")) {
    return { message, code: "ACCESS_DENIED" };
  }
  if (message.includes("not found") || message.includes("404")) {
    return { message, code: "NOT_FOUND" };
  }

  return { message, code: "UNKNOWN" };
}

// Helper to build auth params for API calls (exported for use by worldStore)
export function getAuthParams(state: GitState): { workspaceId: number; installationId?: number; authType?: AuthType } | null {
  if (!state.currentWorkspaceId) return null;

  if (state.authType === "pat") {
    return {
      workspaceId: state.currentWorkspaceId,
      authType: "pat",
    };
  }

  if (state.selectedInstallationId) {
    return {
      workspaceId: state.currentWorkspaceId,
      installationId: state.selectedInstallationId,
    };
  }

  return null;
}

interface GitState {
  // Current context
  currentWorkspaceId: number | null;

  // Installation state (workspace level)
  availableInstallations: GitHubInstallation[];
  connectedInstallations: GitHubInstallation[];
  selectedInstallationId: number | null;

  // Repo state (project level)
  repos: GitHubRepo[];
  selectedRepo: string | null; // "owner/repo" format

  // Branch/commit/PR state
  branches: CacheEntry<GitHubBranch[]> | null;
  selectedBranch: string | null;
  commits: CacheEntry<GitHubCommit[]> | null;
  pullRequests: CacheEntry<GitHubPullRequest[]> | null;

  // Pagination
  branchesPage: number;
  hasMoreBranches: boolean;
  commitsPage: number;
  hasMoreCommits: boolean;
  pullRequestsPage: number;
  hasMorePullRequests: boolean;

  // UI state
  tabState: GitTabState;
  isLoading: boolean;
  isSaving: boolean;
  error: GitError | null;

  // PAT state
  authType: AuthType | "none";
  workspacePAT: GitHubPAT | null;
  patValidating: boolean;
  patError: string | null;

  // Actions - Installations
  fetchAvailableInstallations: () => Promise<void>;
  fetchConnectedInstallations: (workspaceId: number) => Promise<void>;
  connectInstallation: (workspaceId: number, installationId: number) => Promise<GitHubInstallation | null>;
  disconnectInstallation: (workspaceId: number, installationDbId: number) => Promise<boolean>;
  selectInstallation: (installationId: number | null) => void;

  // Actions - Repos
  fetchRepos: (workspaceId: number, installationId: number) => Promise<void>;
  fetchPATRepos: (workspaceId: number) => Promise<void>;
  selectRepo: (repo: string | null) => void;
  connectRepo: (repo: string) => Promise<void>;

  // Actions - Branches (use current auth context)
  fetchBranches: (owner: string, repo: string, reset?: boolean) => Promise<void>;
  loadMoreBranches: (owner: string, repo: string) => Promise<void>;
  selectBranch: (branch: string | null) => void;
  createBranch: (owner: string, repo: string, name: string, fromBranch: string) => Promise<GitHubBranch | null>;

  // Actions - Commits (use current auth context)
  fetchCommits: (owner: string, repo: string, branch: string, reset?: boolean) => Promise<void>;
  loadMoreCommits: (owner: string, repo: string, branch: string) => Promise<void>;

  // Actions - Pull Requests (use current auth context)
  fetchPullRequests: (owner: string, repo: string, reset?: boolean) => Promise<void>;
  loadMorePullRequests: (owner: string, repo: string) => Promise<void>;
  createPullRequest: (owner: string, repo: string, title: string, body: string, head: string, base: string, draft?: boolean) => Promise<GitHubPullRequest | null>;

  // Actions - State management
  setTabState: (state: GitTabState) => void;
  clearError: () => void;
  reset: () => void;
  initializeForProject: (workspaceId: number, githubRepo: string | null, installationId: number | null) => Promise<void>;
  invalidateBranchesCache: () => void;
  invalidatePullRequestsCache: () => void;
  refreshAll: (owner: string, repo: string) => Promise<void>;

  // Actions - PAT
  setAuthType: (type: AuthType) => void;
  fetchWorkspacePAT: (workspaceId: number) => Promise<GitHubPAT | null>;
  savePAT: (workspaceId: number, token: string) => Promise<boolean>;
  deletePAT: (workspaceId: number) => Promise<boolean>;
  validatePAT: (token: string) => Promise<GitHubPATValidation | null>;
}

// ============================================================================
// Cache TTLs (milliseconds)
// ============================================================================

const PAGE_SIZE = {
  branches: 30,
  commits: 20,
  pullRequests: 20,
};

const CACHE_TTL = {
  repos: 5 * 60 * 1000, // 5 min
  branches: 1 * 60 * 1000, // 1 min
  commits: 30 * 1000, // 30 sec
  pullRequests: 1 * 60 * 1000, // 1 min
};

function isCacheValid<T>(cache: CacheEntry<T> | null, ttl: number): boolean {
  if (!cache) return false;
  return Date.now() - cache.fetchedAt < ttl && !cache.isStale;
}

// ============================================================================
// Store
// ============================================================================

export const useGitStore = create<GitState>()(
  subscribeWithSelector(
    persist(
      (set, get) => ({
    // Initial state
    currentWorkspaceId: null,
    availableInstallations: [],
    connectedInstallations: [],
    selectedInstallationId: null,
    repos: [],
    selectedRepo: null,
    branches: null,
    selectedBranch: null,
    commits: null,
    pullRequests: null,
    branchesPage: 1,
    hasMoreBranches: true,
    commitsPage: 1,
    hasMoreCommits: true,
    pullRequestsPage: 1,
    hasMorePullRequests: true,
    tabState: "NO_CONNECTION",
    isLoading: false,
    isSaving: false,
    error: null,

    // PAT state
    authType: "installation",
    workspacePAT: null,
    patValidating: false,
    patError: null,

    // ---- Installation Actions ----

    fetchAvailableInstallations: async () => {
      set({ isLoading: true, error: null });
      try {
        const installations = await githubApi.listAvailableInstallations();
        set({ availableInstallations: installations, isLoading: false });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    fetchConnectedInstallations: async (workspaceId: number) => {
      set({ isLoading: true, error: null });
      try {
        const installations = await githubApi.listWorkspaceInstallations(workspaceId);
        set({
          connectedInstallations: installations,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
          tabState: "ERROR",
        });
      }
    },

    connectInstallation: async (workspaceId: number, installationId: number) => {
      set({ isSaving: true, error: null });
      try {
        const installation = await githubApi.connectInstallation(workspaceId, installationId);
        set((state) => ({
          connectedInstallations: [...state.connectedInstallations, installation],
          selectedInstallationId: installation.id,
          authType: "installation",
          tabState: "NO_REPO",
          isSaving: false,
        }));
        return installation;
      } catch (error) {
        set({
          error: parseError(error),
          isSaving: false,
        });
        return null;
      }
    },

    disconnectInstallation: async (workspaceId: number, installationDbId: number) => {
      set({ isSaving: true, error: null });
      try {
        await githubApi.disconnectInstallation(workspaceId, installationDbId);
        set((state) => {
          const remaining = state.connectedInstallations.filter((i) => i.id !== installationDbId);
          return {
            connectedInstallations: remaining,
            selectedInstallationId: remaining.length > 0 ? remaining[0].id : null,
            tabState: remaining.length > 0 ? state.tabState : "NO_CONNECTION",
            isSaving: false,
          };
        });
        return true;
      } catch (error) {
        set({
          error: parseError(error),
          isSaving: false,
        });
        return false;
      }
    },

    selectInstallation: (installationId: number | null) => {
      set({
        selectedInstallationId: installationId,
        repos: [],
        selectedRepo: null,
        branches: null,
        selectedBranch: null,
        commits: null,
        pullRequests: null,
      });
    },

    // ---- Repo Actions ----

    fetchRepos: async (workspaceId: number, installationId: number) => {
      set({ isLoading: true, error: null });
      try {
        const repos = await githubApi.listRepos(workspaceId, installationId);
        set({ repos, isLoading: false });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
          tabState: "ERROR",
        });
      }
    },

    fetchPATRepos: async (workspaceId: number) => {
      set({ isLoading: true, error: null });
      try {
        const repos = await githubApi.listPATRepos(workspaceId);
        set({ repos, isLoading: false });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
          tabState: "ERROR",
        });
      }
    },

    selectRepo: (repo: string | null) => {
      set({
        selectedRepo: repo,
        branches: null,
        selectedBranch: null,
        commits: null,
        pullRequests: null,
        branchesPage: 1,
        hasMoreBranches: true,
        commitsPage: 1,
        hasMoreCommits: true,
        pullRequestsPage: 1,
        hasMorePullRequests: true,
        tabState: repo ? "LOADING" : "NO_REPO",
      });
    },

    connectRepo: async (repo: string) => {
      const state = get();
      set({
        selectedRepo: repo,
        branches: null,
        selectedBranch: null,
        commits: null,
        pullRequests: null,
        branchesPage: 1,
        hasMoreBranches: true,
        commitsPage: 1,
        hasMoreCommits: true,
        pullRequestsPage: 1,
        hasMorePullRequests: true,
        tabState: "LOADING",
        isLoading: true,
      });

      // Parse owner/repo
      const [owner, repoName] = repo.split("/");
      if (!owner || !repoName) {
        set({ tabState: "NO_REPO", isLoading: false, error: { message: "Invalid repo format", code: "UNKNOWN" } });
        return;
      }

      // Fetch branches
      try {
        await state.fetchBranches(owner, repoName, true);
        const newState = get();
        if (newState.selectedBranch) {
          await Promise.all([
            state.fetchCommits(owner, repoName, newState.selectedBranch, true),
            state.fetchPullRequests(owner, repoName, true),
          ]);
        }
        set({ tabState: "READY", isLoading: false });
      } catch (error) {
        set({
          error: parseError(error),
          tabState: "ERROR",
          isLoading: false,
        });
      }
    },

    // ---- Branch Actions ----

    fetchBranches: async (owner: string, repo: string, reset: boolean = false) => {
      const state = get();
      if (!reset && isCacheValid(state.branches, CACHE_TTL.branches)) {
        return;
      }

      const auth = getAuthParams(state);
      if (!auth) {
        set({ error: { message: "No authentication configured", code: "UNKNOWN" }, tabState: "ERROR" });
        return;
      }

      set({ isLoading: true, error: null, branchesPage: 1 });
      try {
        const branches = await githubApi.listBranches(owner, repo, auth, PAGE_SIZE.branches, 1);
        set({
          branches: { data: branches, fetchedAt: Date.now(), isStale: false },
          hasMoreBranches: branches.length >= PAGE_SIZE.branches,
          isLoading: false,
          tabState: "READY",
        });

        // Auto-select default branch if none selected
        if (!state.selectedBranch && branches.length > 0) {
          try {
            const defaultBranch = await githubApi.getDefaultBranch(owner, repo, auth);
            const branch = branches.find((b) => b.name === defaultBranch) || branches[0];
            set({ selectedBranch: branch.name });
          } catch {
            // Fallback to first branch
            set({ selectedBranch: branches[0].name });
          }
        }
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
          tabState: "ERROR",
        });
      }
    },

    loadMoreBranches: async (owner: string, repo: string) => {
      const state = get();
      if (!state.hasMoreBranches || state.isLoading) return;

      const auth = getAuthParams(state);
      if (!auth) return;

      const nextPage = state.branchesPage + 1;
      set({ isLoading: true, error: null });

      try {
        const moreBranches = await githubApi.listBranches(owner, repo, auth, PAGE_SIZE.branches, nextPage);
        set((state) => ({
          branches: state.branches
            ? { ...state.branches, data: [...state.branches.data, ...moreBranches] }
            : { data: moreBranches, fetchedAt: Date.now(), isStale: false },
          branchesPage: nextPage,
          hasMoreBranches: moreBranches.length >= PAGE_SIZE.branches,
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    selectBranch: (branch: string | null) => {
      set({
        selectedBranch: branch,
        commits: null,
        commitsPage: 1,
        hasMoreCommits: true,
      });
    },

    createBranch: async (owner: string, repo: string, name: string, fromBranch: string) => {
      const state = get();
      const auth = getAuthParams(state);
      if (!auth) return null;

      set({ isSaving: true, error: null });
      try {
        const branch = await githubApi.createBranch(owner, repo, auth, name, fromBranch);
        set((state) => ({
          // Optimistically add branch, but mark cache as stale for next refresh
          branches: state.branches
            ? { ...state.branches, data: [...state.branches.data, branch], isStale: true }
            : { data: [branch], fetchedAt: Date.now(), isStale: true },
          selectedBranch: branch.name,
          // Clear commits for new branch
          commits: null,
          commitsPage: 1,
          hasMoreCommits: true,
          isSaving: false,
        }));
        return branch;
      } catch (error) {
        set({
          error: parseError(error),
          isSaving: false,
        });
        return null;
      }
    },

    // ---- Commit Actions ----

    fetchCommits: async (owner: string, repo: string, branch: string, reset: boolean = false) => {
      const state = get();
      if (!reset && isCacheValid(state.commits, CACHE_TTL.commits)) {
        return;
      }

      const auth = getAuthParams(state);
      if (!auth) return;

      set({ isLoading: true, error: null, commitsPage: 1 });
      try {
        const commits = await githubApi.listCommits(owner, repo, auth, branch, PAGE_SIZE.commits, 1);
        set({
          commits: { data: commits, fetchedAt: Date.now(), isStale: false },
          hasMoreCommits: commits.length >= PAGE_SIZE.commits,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    loadMoreCommits: async (owner: string, repo: string, branch: string) => {
      const state = get();
      if (!state.hasMoreCommits || state.isLoading) return;

      const auth = getAuthParams(state);
      if (!auth) return;

      const nextPage = state.commitsPage + 1;
      set({ isLoading: true, error: null });

      try {
        const moreCommits = await githubApi.listCommits(owner, repo, auth, branch, PAGE_SIZE.commits, nextPage);
        set((state) => ({
          commits: state.commits
            ? { ...state.commits, data: [...state.commits.data, ...moreCommits] }
            : { data: moreCommits, fetchedAt: Date.now(), isStale: false },
          commitsPage: nextPage,
          hasMoreCommits: moreCommits.length >= PAGE_SIZE.commits,
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    // ---- Pull Request Actions ----

    fetchPullRequests: async (owner: string, repo: string, reset: boolean = false) => {
      const state = get();
      if (!reset && isCacheValid(state.pullRequests, CACHE_TTL.pullRequests)) {
        return;
      }

      const auth = getAuthParams(state);
      if (!auth) return;

      set({ isLoading: true, error: null, pullRequestsPage: 1 });
      try {
        const prs = await githubApi.listPullRequests(owner, repo, auth, "open", PAGE_SIZE.pullRequests, 1);
        set({
          pullRequests: { data: prs, fetchedAt: Date.now(), isStale: false },
          hasMorePullRequests: prs.length >= PAGE_SIZE.pullRequests,
          isLoading: false,
        });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    loadMorePullRequests: async (owner: string, repo: string) => {
      const state = get();
      if (!state.hasMorePullRequests || state.isLoading) return;

      const auth = getAuthParams(state);
      if (!auth) return;

      const nextPage = state.pullRequestsPage + 1;
      set({ isLoading: true, error: null });

      try {
        const morePRs = await githubApi.listPullRequests(owner, repo, auth, "open", PAGE_SIZE.pullRequests, nextPage);
        set((state) => ({
          pullRequests: state.pullRequests
            ? { ...state.pullRequests, data: [...state.pullRequests.data, ...morePRs] }
            : { data: morePRs, fetchedAt: Date.now(), isStale: false },
          pullRequestsPage: nextPage,
          hasMorePullRequests: morePRs.length >= PAGE_SIZE.pullRequests,
          isLoading: false,
        }));
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    createPullRequest: async (owner: string, repo: string, title: string, body: string, head: string, base: string, draft: boolean = false) => {
      const state = get();
      const auth = getAuthParams(state);
      if (!auth) return null;

      set({ isSaving: true, error: null });
      try {
        const pr = await githubApi.createPullRequest(owner, repo, auth, title, body, head, base, draft);
        set((state) => ({
          // Optimistically add PR, mark cache as stale for next refresh
          pullRequests: state.pullRequests
            ? { ...state.pullRequests, data: [pr, ...state.pullRequests.data], isStale: true }
            : { data: [pr], fetchedAt: Date.now(), isStale: true },
          isSaving: false,
        }));
        return pr;
      } catch (error) {
        set({
          error: parseError(error),
          isSaving: false,
        });
        return null;
      }
    },

    // ---- State Management ----

    setTabState: (state: GitTabState) => set({ tabState: state }),

    clearError: () => set({ error: null, patError: null }),

    reset: () =>
      set({
        currentWorkspaceId: null,
        availableInstallations: [],
        connectedInstallations: [],
        selectedInstallationId: null,
        repos: [],
        selectedRepo: null,
        branches: null,
        selectedBranch: null,
        commits: null,
        pullRequests: null,
        branchesPage: 1,
        hasMoreBranches: true,
        commitsPage: 1,
        hasMoreCommits: true,
        pullRequestsPage: 1,
        hasMorePullRequests: true,
        tabState: "NO_CONNECTION",
        isLoading: false,
        isSaving: false,
        error: null,
        // PAT state
        authType: "installation",
        workspacePAT: null,
        patValidating: false,
        patError: null,
      }),

    initializeForProject: async (workspaceId: number, githubRepo: string | null, installationId: number | null) => {
      const state = get();

      // Reset state for new project
      set({
        currentWorkspaceId: workspaceId,
        selectedRepo: githubRepo,
        selectedInstallationId: installationId,
        branches: null,
        selectedBranch: null,
        commits: null,
        pullRequests: null,
        branchesPage: 1,
        hasMoreBranches: true,
        commitsPage: 1,
        hasMoreCommits: true,
        pullRequestsPage: 1,
        hasMorePullRequests: true,
        isLoading: true,
        error: null,
        patError: null,
      });

      try {
        // Fetch both installations and PAT in parallel
        const [installations, pat] = await Promise.all([
          githubApi.listWorkspaceInstallations(workspaceId).catch(() => []),
          state.fetchWorkspacePAT(workspaceId),
        ]);

        const newState = get();
        set({ connectedInstallations: installations });

        // Determine auth type based on what's available
        const hasPAT = !!pat;
        const hasInstallations = installations.length > 0;

        if (!hasInstallations && !hasPAT) {
          set({ tabState: "NO_CONNECTION", isLoading: false });
          return;
        }

        // If there's an installation ID provided, use installation auth
        // Otherwise, prefer PAT if available
        let activeAuthType: AuthType = "installation";
        let activeInstallationId: number | null = null;

        if (installationId && hasInstallations) {
          const found = installations.find((i) => i.id === installationId);
          if (found) {
            activeInstallationId = found.id;
            activeAuthType = "installation";
          }
        } else if (hasInstallations) {
          activeInstallationId = installations[0].id;
          activeAuthType = "installation";
        } else if (hasPAT) {
          activeAuthType = "pat";
        }

        set({
          selectedInstallationId: activeInstallationId,
          authType: activeAuthType,
        });

        if (!githubRepo) {
          set({ tabState: "NO_REPO", isLoading: false });
          return;
        }

        // Parse owner/repo
        const [owner, repo] = githubRepo.split("/");
        if (!owner || !repo) {
          set({ tabState: "NO_REPO", isLoading: false });
          return;
        }

        // Fetch branches
        await state.fetchBranches(owner, repo, true);

        const finalState = get();
        if (finalState.selectedBranch) {
          await Promise.all([
            state.fetchCommits(owner, repo, finalState.selectedBranch, true),
            state.fetchPullRequests(owner, repo, true),
          ]);
        }

        set({ tabState: "READY", isLoading: false });
      } catch (error) {
        set({
          error: parseError(error),
          tabState: "ERROR",
          isLoading: false,
        });
      }
    },

    invalidateBranchesCache: () => {
      set((state) => ({
        branches: state.branches ? { ...state.branches, isStale: true } : null,
      }));
    },

    invalidatePullRequestsCache: () => {
      set((state) => ({
        pullRequests: state.pullRequests ? { ...state.pullRequests, isStale: true } : null,
      }));
    },

    refreshAll: async (owner: string, repo: string) => {
      const state = get();

      // Force refresh all data by marking caches as stale
      set({
        branches: null,
        commits: null,
        pullRequests: null,
        isLoading: true,
      });

      try {
        await state.fetchBranches(owner, repo, true);
        const newState = get();
        if (newState.selectedBranch) {
          await Promise.all([
            state.fetchCommits(owner, repo, newState.selectedBranch, true),
            state.fetchPullRequests(owner, repo, true),
          ]);
        }
        set({ isLoading: false });
      } catch (error) {
        set({
          error: parseError(error),
          isLoading: false,
        });
      }
    },

    // ---- PAT Actions ----

    setAuthType: (type: AuthType) => {
      set({ authType: type, patError: null });
    },

    fetchWorkspacePAT: async (workspaceId: number) => {
      set({ patError: null });
      try {
        const pat = await githubApi.getWorkspacePAT(workspaceId);
        set({ workspacePAT: pat });
        // Sync git identity to keychain (backfill for existing PATs)
        if (isTauri() && pat.git_name) {
          try {
            const { invoke } = await import("@tauri-apps/api/core");
            await invoke("set_secret", { key: `gh_git_name_ws_${workspaceId}`, value: pat.git_name });
            if (pat.git_email) {
              await invoke("set_secret", { key: `gh_git_email_ws_${workspaceId}`, value: pat.git_email });
            }
          } catch {
            // Non-fatal
          }
        }
        return pat;
      } catch (error) {
        // 404 is expected if no PAT exists on the backend.
        // In Tauri, fall back to checking the OS keychain — local workspaces
        // store PATs there but may not have a matching backend record.
        if (isTauri()) {
          try {
            const { invoke } = await import("@tauri-apps/api/core");
            const token = await invoke<string | null>("get_secret", { key: `gh_pat_ws_${workspaceId}` });
            if (token && token.trim().length > 0) {
              const gitName = await invoke<string | null>("get_secret", { key: `gh_git_name_ws_${workspaceId}` }).catch(() => null);
              const gitEmail = await invoke<string | null>("get_secret", { key: `gh_git_email_ws_${workspaceId}` }).catch(() => null);
              const keychainPat: GitHubPAT = {
                id: 0,
                workspaceId: workspaceId,
                tokenHint: token.slice(0, 4) + ".." + token.slice(-4),
                scopes: [],
                createdAt: new Date().toISOString(),
                git_name: gitName ?? undefined,
                git_email: gitEmail ?? undefined,
              };
              set({ workspacePAT: keychainPat });
              return keychainPat;
            }
          } catch {
            // Keychain read failed, fall through to null
          }
        }

        if ((error as Error).message?.includes("404")) {
          set({ workspacePAT: null });
        } else {
          set({ patError: (error as Error).message });
        }
        return null;
      }
    },

    savePAT: async (workspaceId: number, token: string) => {
      set({ isSaving: true, patError: null });
      try {
        const pat = await githubApi.createWorkspacePAT(workspaceId, token);
        set({
          workspacePAT: pat,
          authType: "pat",
          isSaving: false,
          tabState: "NO_REPO", // Move to repo selection after PAT saved
        });
        // Also store in OS keychain for local git operations (Tauri only)
        if (isTauri()) {
          try {
            const { invoke } = await import("@tauri-apps/api/core");
            await invoke("set_secret", { key: `gh_pat_ws_${workspaceId}`, value: token });
            // Store git identity for credential injection into workers and terminals
            if (pat.git_name) {
              await invoke("set_secret", { key: `gh_git_name_ws_${workspaceId}`, value: pat.git_name });
            }
            if (pat.git_email) {
              await invoke("set_secret", { key: `gh_git_email_ws_${workspaceId}`, value: pat.git_email });
            }
          } catch {
            // Non-fatal: local git auth won't work but cloud git still functions
          }
        }
        return true;
      } catch (error) {
        set({
          patError: (error as Error).message,
          isSaving: false,
        });
        return false;
      }
    },

    deletePAT: async (workspaceId: number) => {
      const state = get();
      set({ isSaving: true, patError: null });
      try {
        await githubApi.deleteWorkspacePAT(workspaceId);
        // Check if we have installations to fall back to
        const hasInstallations = state.connectedInstallations.length > 0;
        set({
          workspacePAT: null,
          isSaving: false,
          authType: hasInstallations ? "installation" : "none",
          tabState: hasInstallations ? "NO_REPO" : "NO_CONNECTION",
        });
        // Also remove from OS keychain (Tauri only)
        if (isTauri()) {
          try {
            const { invoke } = await import("@tauri-apps/api/core");
            await invoke("delete_secret", { key: `gh_pat_ws_${workspaceId}` });
            await invoke("delete_secret", { key: `gh_git_name_ws_${workspaceId}` }).catch(() => {});
            await invoke("delete_secret", { key: `gh_git_email_ws_${workspaceId}` }).catch(() => {});
          } catch {
            // Non-fatal
          }
        }
        return true;
      } catch (error) {
        set({
          patError: (error as Error).message,
          isSaving: false,
        });
        return false;
      }
    },

    validatePAT: async (token: string) => {
      set({ patValidating: true, patError: null });
      try {
        const result = await githubApi.validatePAT(token);
        set({ patValidating: false });
        if (!result.valid) {
          set({ patError: result.error || "Invalid token" });
        }
        return result;
      } catch (error) {
        set({
          patValidating: false,
          patError: (error as Error).message,
        });
        return null;
      }
    },
  }),
      {
        name: "factory-git-selections",
        version: 1,
        partialize: (state) => ({
          selectedRepo: state.selectedRepo,
          selectedInstallationId: state.selectedInstallationId,
          authType: state.authType,
          currentWorkspaceId: state.currentWorkspaceId,
        }),
      }
    )
  )
);
